import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type PetIssue = {
  petIssueID: number;
  petIssueMainID: number;
  petIssueMainType: string;
  uniqueKey: string;
};

export type PetImageObj = {
  orignalImage: string;
  croppedImage: string;
  proportion: { zoom: string; x: string; y: string; rotation: string };
  oldProportion?: { zoom: string; x: string; y: string; rotation: string };
};

export interface StepType {
  name: string;
  maxSteps: number;
}

export type PetProfileSlice = {
  DOB: string;
  PetName: string;
  PetAge: number | null;
  Gender: string;
  PetImage?: PetImageObj;
  EditablePetImage: PetImageObj;
  PetSize: string;
  PetBreed: number;
  PetWeightCategory: number;
  PetWeight: string;
  PetIssues: PetIssue[];
  PetAllergies: string[];
  PetDietIds: string[];
  IsDogProfile: boolean;
  PetDemographicId: number;
  PetDietDetailId: number;
  ExistingPetProfileGroupId: number;
  PetProfileGroups: Array<any>;
  PetSpecieId: string;
  SelectedPetProfileGroupId: string;
  PrevSelectedPetProfileGroupId?: string;
  activeStep: number;
  innerSteps: number[];
  steps: StepType[];
  isEdit: boolean;
  petProfileId: string;
  customerId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  webAlias?: string;
  hideTopLoader?: boolean;
  isReset?: boolean;
  comingFromRecommendations: boolean;
  previousEmail: string;
  addProfile: boolean;
  addProfileKey: number;
  profileCreatedBefore: boolean;
};

const loadState = (): PetProfileSlice | undefined => {
  if (typeof window === 'undefined') {
    // Return undefined when running on the server
    return undefined;
  }

  try {
    const serializedState = localStorage.getItem('petProfile');
    return serializedState ? JSON.parse(serializedState) : undefined;
  } catch (error) {
    console.error('Failed to load state from localStorage:', error);
    return undefined;
  }
};

const saveState = (state: PetProfileSlice) => {
  if (typeof window === 'undefined') {
    // Skip saving state on the server
    return;
  }

  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('petProfile', serializedState);
  } catch (error) {
    console.error('Failed to save state to localStorage:', error);
  }
};

// Initial steps configuration
export const initialSteps: StepType[] = [
  { name: 'About', maxSteps: 3 },
  { name: 'Breed', maxSteps: 2 },
  { name: 'Allergies', maxSteps: 2 },
  { name: 'Issues', maxSteps: 2 },
  { name: 'Extra', maxSteps: 4 },
];

const getFreshInitialState = (): PetProfileSlice => ({
  DOB: '',
  PetName: '',
  PetAge: 5,
  Gender: 'M',
  PetImage: {
    orignalImage: '',
    croppedImage: '',
    proportion: { zoom: '0', x: '0', y: '0', rotation: '0' },
  },
  EditablePetImage: {
    orignalImage: '',
    croppedImage: '',
    proportion: { zoom: '0', x: '0', y: '0', rotation: '0' },
  },
  PetSize: '',
  PetBreed: 0,
  PetWeightCategory: 0,
  PetWeight: '0',
  PetIssues: [],
  PetAllergies: [],
  PetDietIds: [],
  IsDogProfile: true,
  PetDemographicId: 0,
  PetDietDetailId: 0,
  ExistingPetProfileGroupId: 0,
  PetProfileGroups: [],
  PetSpecieId: '1',
  SelectedPetProfileGroupId: '',
  PrevSelectedPetProfileGroupId: '',
  activeStep: 0,
  innerSteps: initialSteps.map(() => 1),
  steps: initialSteps,
  isEdit: false,
  petProfileId: '',
  hideTopLoader: false,
  isReset: false,
  comingFromRecommendations: false,
  addProfile: false,
  previousEmail: '',
  customerId: '',
  addProfileKey: 0,
  profileCreatedBefore: false,
});

export const petProfileInitialState: PetProfileSlice = loadState() || getFreshInitialState();

const petProfileSlice = createSlice({
  name: 'petProfileSlice',
  initialState: petProfileInitialState,
  reducers: {
    updatePetProfile(state, { payload }: PayloadAction<Partial<PetProfileSlice>>) {
      Object.assign(state, { ...payload });
      saveState(state);
    },
    setActiveStep(state, action: PayloadAction<number>) {
      state.activeStep = action.payload;
      saveState(state);
    },
    setInnerSteps(state, action: PayloadAction<number[]>) {
      state.innerSteps = action.payload;
      saveState(state);
    },
    incrementInnerStep(state, action: PayloadAction<number>) {
      const stepIndex = action.payload;
      const maxSteps = state.steps[stepIndex].maxSteps;

      if (state.innerSteps[stepIndex] < maxSteps) {
        state.innerSteps[stepIndex] += 1;
      } else if (stepIndex === state.activeStep && state.activeStep < state.steps.length - 1) {
        state.activeStep += 1;
        state.innerSteps[state.activeStep] = 1; // Start new active step at 1
      }
      saveState(state);
    },
    decrementInnerStep(state, action: PayloadAction<number>) {
      const stepIndex = action.payload;

      if (state.innerSteps[stepIndex] > 1) {
        state.innerSteps[stepIndex] -= 1;
      } else if (stepIndex === state.activeStep && state.activeStep > 0) {
        state.activeStep -= 1;
        const prevMaxStep = state.steps[state.activeStep].maxSteps;
        state.innerSteps[state.activeStep] = prevMaxStep;
      }
      saveState(state);
    },
    clearPetCreationState: () => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('petProfile');
      }

      return { ...getFreshInitialState(), isReset: true };
    },
    resetState: () => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('petProfile');
      }
      return getFreshInitialState();
    },
  },
});

export const {
  clearPetCreationState,
  updatePetProfile,
  setActiveStep,
  setInnerSteps,
  incrementInnerStep,
  decrementInnerStep,
  resetState,
} = petProfileSlice.actions;

export default petProfileSlice;
